import { formatPriceCurrency, getCurrency } from 'utils/utils';
import {
  FlProposal,
  HotProposal,
  Proposal,
  ReportsList,
  TransferProposal,
  TripSlotRequirement,
} from 'ts/shared/interfaces/CreateProposalParams';
import {
  ProposalData,
  ProposalFlight,
  ProposalHotel,
  ProposalList,
} from 'ts/shared/interfaces/ProposalData';
import { orderGroupColors } from './my-order';
import {
  buildPTypeBySearchToken,
  buildTripSlotContexts,
  getCheckIn,
  getCheckOut,
  IdbDraftProducts,
  mergeIdbDraftsIntoProposal,
  toDayKey,
  tripRowKeys,
  TripBuilderTransferCandidate,
  TripSlotContext,
  getHotelSlotKey,
} from './tripBuilder';
import {
  ClaudeHotelInfoConfig,
  applyClaudeHotelInfoConfigToExternalParam,
} from './claudeProposalInformation';
import { HotelInfoConfig, applyHotelInfoConfigToExternalParam } from './hotelProposalInformation';
import { getAdjustedProposalCancellationDate, isValidProposalCancellationDate } from './proposal';
import type { TripBuilderIdbDrafts } from './tripResultBuilderIdb';
import { dateFormat, getBounds } from './utils';

export type TripPreviewFlightCard = {
  key: string;
  route: string;
  departureLabel?: string;
  arrivalLabel?: string;
  departureTime?: string;
  arrivalTime?: string;
  durationLabel?: string;
  arrivalDetailLabel?: string;
  flightInfoLabel?: string;
  airlineCode?: string;
  logoCode?: string;
  scheduleLabel: string | null;
  priceLabel: string | null;
  isActive: boolean;
  isRoundTrip: boolean;
  compoundColor?: string;
  searchToken?: string;
  flightId?: string;
};

export type TripPreviewHotelRoomInfo = {
  roomLabel?: string | null;
  boardLabel?: string | null;
  cxlLabel?: string | null;
  cxlType?: 'free' | 'full' | null;
  cxlDays?: number | null;
  priceLabel?: string | null;
};

export type TripPreviewHotelCard = {
  key: string;
  name: string;
  star?: number;
  priceLabel: string | null;
  isActive: boolean;
  logoCode?: string;
  nights?: number | null;
  rooms?: TripPreviewHotelRoomInfo[];
  serpHotel?: any;
  tripAdvisorHotel?: any;
  hotelInformatiom?: any;
  aI_HotelInformatiom?: any;
  itemCode?: string;
  hotelIndex?: number;
  supplierCode?: string;
  bpid?: string;
};

export type TripPreviewTransferCard = {
  key: string;
  name: string;
  pickUpLabel: string | null;
  dropOffLabel: string | null;
  scheduleLabel: string | null;
  priceLabel: string | null;
  isActive: boolean;
  searchToken?: string;
  priceKey?: string;
};

export type TripPreviewSection = {
  requirementLabel: string;
  sectionTitle?: string;
  transitionKey?: string;
  flights: TripPreviewFlightCard[];
  hotels: TripPreviewHotelCard[];
  transfers: TripPreviewTransferCard[];
};

export type TripPreviewChainNode = {
  dateLabel: string;
  city: string;
  checkIn: string;
  checkOut: string;
  slotKey: string;
  hotelSection: TripPreviewSection | null;
  transferSection: TripPreviewSection | null;
  middleSections: TripPreviewSection[];
};

export type TripPreviewActiveState = {
  activeBefore: Record<string, string>;
  activeAfter: Record<string, string>;
  activeMiddle: Record<string, string>;
  activeHotel: Record<string, string>;
  activeTransfer: Record<string, string>;
};

export const emptyTripPreviewActiveState: TripPreviewActiveState = {
  activeBefore: {},
  activeAfter: {},
  activeMiddle: {},
  activeHotel: {},
  activeTransfer: {},
};

export type TripPreviewDateBadge = {
  day: string;
  month: string;
  weekDay: string;
};

export type TripPreviewSlotView = {
  rootSlotKey: string;
  startDateBadge: TripPreviewDateBadge | null;
  endDateBadge: TripPreviewDateBadge | null;
  beforeSection: TripPreviewSection | null;
  afterSection: TripPreviewSection | null;
  chainNodes: TripPreviewChainNode[];
};

export type TripPreviewModel = {
  title: string;
  displayTotalDelta: boolean;
  totalLabel: string | null;
  totalCurrency: string;
  slots: TripPreviewSlotView[];
};

export const pTypeToTripRequirement = (pType?: string): TripSlotRequirement => {
  if (pType === 'Optional') {
    return 'optional';
  }
  if (pType === 'MandatoryFirst' || pType === 'ManadatoryFirst') {
    return 'mandatory-first';
  }
  return 'mandatory';
};

const requirementLabels: Record<TripSlotRequirement, string> = {
  optional: 'Optional',
  mandatory: 'Mandatory',
  'mandatory-first': 'Mandatory First',
};

type ProductWithMeta = ProposalList & {
  pType?: string;
  listOfImages?: Array<{ code?: string; name?: string }>;
};

const getRequirementLabel = (value: TripSlotRequirement) => requirementLabels[value] ?? 'Mandatory';

const parseTripBuilderExternalParam = (
  externalParam_1?: string,
): Record<string, unknown> | null => {
  if (!externalParam_1) {
    return null;
  }
  try {
    const data = JSON.parse(externalParam_1);
    return data.isTripBuilder ? data : null;
  } catch {
    return null;
  }
};

const formatSlotDate = (value: string, locale: string) =>
  value ? dateFormat(value, 'dd MMM yyyy', locale) : '-';
const formatFlightSchedule = (departureDateTime?: string, arrivalDateTime?: string) => {
  if (!departureDateTime || !arrivalDateTime) {
    return '';
  }
  const depDate = dateFormat(departureDateTime, 'ddMMM', 'en').toUpperCase();
  const depTime = dateFormat(departureDateTime, 'HH:mm', 'en');
  const arrTime = dateFormat(arrivalDateTime, 'HH:mm', 'en');
  const arrDateSuffix = ` ${dateFormat(arrivalDateTime, 'ddMMM', 'en').toUpperCase()}`;
  return `${depDate} ${depTime} - ${arrTime}${arrDateSuffix}`;
};

const getFlightCityLabel = (airport?: { cityDesc?: string; name?: string }) =>
  airport?.cityDesc || airport?.name || undefined;

const formatFlightDurationLabel = (elapsedDTime?: number, bound?: { gdsFlyAirs?: unknown[] }) => {
  if (!elapsedDTime) {
    return undefined;
  }
  const hours = Math.round(elapsedDTime / 60);
  const stops = Math.max(0, (bound?.gdsFlyAirs?.length || 1) - 1);
  const stopLabel = stops === 0 ? 'direct' : `${stops} stop${stops > 1 ? 's' : ''}`;
  return `~${hours}h ${stopLabel}`;
};

const formatArrivalDetailLabel = (
  departureDateTime?: string,
  arrivalDateTime?: string,
  airportName?: string,
) => {
  const parts: string[] = [];
  if (departureDateTime && arrivalDateTime) {
    const depDay = dateFormat(departureDateTime, 'yyyy-MM-dd', 'en');
    const arrDay = dateFormat(arrivalDateTime, 'yyyy-MM-dd', 'en');
    if (depDay !== arrDay) {
      parts.push('Next day');
    }
  }
  if (airportName) {
    parts.push(airportName);
  }
  return parts.length ? parts.join(' · ') : undefined;
};

const getPreviewFlightKey = (flight: FlProposal, index: number) => `${flight.searchToken}-${index}`;

const getPreviewHotelKey = (hotel: HotProposal, index: number) => `${hotel.searchToken}-${index}`;
const getPreviewTransferKey = (transfer: TransferProposal, index: number) =>
  `${transfer.searchToken}-${index}`;

const getHotelStartsFrom = (hotel: HotProposal) => {
  const prices = (hotel.item.roomClasses || [])
    .map((room: any) => room.price?.value)
    .filter((value: any): value is number => typeof value === 'number' && Number.isFinite(value));
  if (!prices.length) {
    return '-';
  }
  return formatPriceCurrency(
    Math.min(...prices),
    getCurrency(hotel.currency || hotel.item.currency),
  );
};

const getHotelPriceValue = (hotel: HotProposal) => {
  const prices = (hotel.item.roomClasses || [])
    .map((room: any) => room.price?.value)
    .filter((value: any): value is number => typeof value === 'number' && Number.isFinite(value));
  if (prices.length) {
    return Math.min(...prices);
  }
  return hotel.price || 0;
};

const getHotelPriceForRoom = (hotel: HotProposal, roomIndex: number) => {
  const room = hotel.item?.roomClasses?.[roomIndex];
  if (typeof room?.price?.value === 'number' && Number.isFinite(room.price.value)) {
    return room.price.value;
  }
  return getHotelPriceValue(hotel);
};

const getTransferLocationLabel = (details?: {
  airport?: { desc?: string; code?: string };
  city?: { desc?: string };
  address?: string;
}) => {
  if (!details) {
    return null;
  }
  return (
    details.airport?.desc || details.airport?.code || details.city?.desc || details.address || null
  );
};

const getProductSearchToken = (
  product: ProposalList,
  hotel?: ProposalHotel,
  flight?: ProposalFlight,
) => {
  if (hotel) {
    return (
      hotel.searchParameters?.searchToken ||
      (hotel as { proposalItem?: { searchToken?: string } }).proposalItem?.searchToken ||
      product.id
    );
  }
  if (flight) {
    return flight.searchParameters?.searchToken || product.id;
  }
  return product.id;
};

const getHotelProductItem = (product: ProposalList) =>
  product.listOfItems.find((item) => item.hotels?.length) ?? product.listOfItems[0];

const getHotelProductHotel = (product: ProposalList): ProposalHotel | undefined =>
  getHotelProductItem(product)?.hotels?.[0];

const resolveProposalListImageUrl = (
  image?: { code?: string; name?: string },
  fallback?: { code?: string; name?: string },
): string => {
  if (image?.code && image.code !== 'HTL') {
    return image.code;
  }
  if (fallback?.code && fallback.code !== 'HTL') {
    return fallback.code;
  }
  return '';
};

const getHotelProductCustomSlotImage = (product: ProposalList): string => {
  const customImage = getHotelProductItem(product)?.listOfImages?.[0];
  if (customImage?.code === 'HTL' && customImage?.name) {
    return customImage.name;
  }
  return '';
};

const getProposalRoomPriceValue = (
  room: ProposalHotel['rooms'][number] | undefined,
  fallback: number,
) => {
  const fromSupplement = +(room?.supplements?.[0]?.code || 0);
  return Number.isFinite(fromSupplement) && fromSupplement > 0 ? fromSupplement : fallback;
};

const getProposalRoomCurrency = (
  room: ProposalHotel['rooms'][number] | undefined,
  fallback: string,
) => (room?.supplements?.[0]?.name || fallback).toLowerCase();

const mapHotelProduct = (product: ProductWithMeta): HotProposal | null => {
  const hotel = getHotelProductHotel(product);
  if (!hotel) {
    return null;
  }

  const room = hotel.rooms[0];
  const defaultCurrency = product.currency?.toLowerCase() || 'usd';
  const defaultPriceValue = product.salePrice ?? product.amount ?? 0;
  const searchToken = getProductSearchToken(product, hotel);
  const savedItem = (hotel as { proposalItem?: HotProposal['item'] }).proposalItem;
  const roomPriceValues = (hotel.rooms || [])
    .map((proposalRoom) => getProposalRoomPriceValue(proposalRoom, 0))
    .filter((value) => value > 0);
  const priceValue = roomPriceValues.length ? Math.min(...roomPriceValues) : defaultPriceValue;
  const currency = getProposalRoomCurrency(room, defaultCurrency);
  const itemImageUrl = resolveProposalListImageUrl(
    getHotelProductItem(product)?.listOfImages?.[0],
    hotel.listOfImages?.[0],
  );
  const bpid = hotel.codes?.find((code) => code.name === 'BPID')?.code || '';

  const mapProposalRoomToRoomClass = (r: ProposalHotel['rooms'][number]) => {
    const roomCurrency = getProposalRoomCurrency(r, defaultCurrency);
    const roomPriceValue = getProposalRoomPriceValue(r, defaultPriceValue);
    return {
      price: {
        value: roomPriceValue,
        [roomCurrency]: roomPriceValue,
        currency: roomCurrency,
      },
      checkIn: r.startDate,
      checkOut: r.endtDate,
      board: r.board,
      hotelRooms: [{ displayName: r.room?.name || '' }],
      cxl: r.cxl,
    };
  };

  const freshItem = {
    roomClasses: (hotel.rooms || []).map(mapProposalRoomToRoomClass),
    addressInfo: {
      city: hotel.city?.name || '',
      address: hotel.adress || '',
      country: hotel.country?.name || '',
    },
    info: {
      cityName: hotel.city?.name || '',
      picture: itemImageUrl,
    },
    item: {
      name: hotel.hotel?.name || hotel.localName,
      star: hotel.star,
      bpid,
    },
    currency,
  } as unknown as HotProposal['item'];

  const item: HotProposal['item'] = savedItem
    ? {
        ...savedItem,
        roomClasses: (savedItem.roomClasses || []).map((roomClass: any, index: number) => {
          const proposalRoom = hotel.rooms?.[index];
          if (!proposalRoom?.supplements?.[0]?.code) {
            return roomClass;
          }
          const roomCurrency = getProposalRoomCurrency(proposalRoom, defaultCurrency);
          const roomPriceValue = getProposalRoomPriceValue(proposalRoom, defaultPriceValue);
          return {
            ...roomClass,
            price: {
              ...roomClass.price,
              value: roomPriceValue,
              [roomCurrency]: roomPriceValue,
              currency: roomCurrency,
            },
          };
        }),
        info: {
          ...savedItem.info,
          picture: savedItem.info?.picture || itemImageUrl,
        },
        item: {
          ...savedItem.item,
          star: savedItem.item?.star ?? hotel.star,
          bpid: savedItem.item?.bpid || bpid,
        },
      }
    : freshItem;

  return {
    item,
    searchToken,
    price: priceValue,
    currency,
    displayPrice: product.displayPrice,
    showCancellationDate: product.showCancellationDate,
    cancellationDateDelta: product.cancellationDateDelta,
    checkIn: room?.startDate,
    checkOut: room?.endtDate,
    serpHotel: hotel.serpHotel,
    tripAdvisorHotel: hotel.tripAdvisorHotel,
    hotelInformation: hotel.hotelInformatiom,
    claudeData: hotel.aI_HotelInformatiom,
    supplierCode: hotel.supplier?.code,
  };
};

type ProposalGdsSegment = ProposalFlight['gdsFlyAirs'][number];

const mapAirportWithCityDesc = (airport?: ProposalGdsSegment['departureAirport']) => {
  if (!airport) {
    return airport;
  }
  return {
    ...airport,
    cityDesc: airport.name,
  };
};

const buildBoundFromGdsSegments = (segments: ProposalGdsSegment[], flightType?: string) => {
  const first = segments[0];
  const last = segments[segments.length - 1];
  return {
    gdsFlyAirs: segments,
    flightType,
    departureDateTime: first?.departureDateTime || '',
    arrivalDateTime: last?.arrivalDateTime || '',
    departureAirport: mapAirportWithCityDesc(first?.departureAirport),
    arrivalAirport: mapAirportWithCityDesc(last?.arrivalAirport),
    airline: first?.airLine ? { code: first.airLine.code } : undefined,
  };
};

const isRoundTripFlightType = (flightType?: string) =>
  flightType === 'RoundTrip' || flightType?.toLowerCase() === 'roundtrip';

const isMultipleDestinationsFlightType = (flightType?: string) =>
  flightType === 'MultipleDestinations' || flightType?.toLowerCase() === 'multipledestinations';

const getGdsSegmentSplitIndex = (segments: ProposalGdsSegment[]) => {
  for (let i = 1; i < segments.length; i++) {
    const prevArrival = segments[i - 1]?.arrivalAirport?.code;
    const nextDeparture = segments[i]?.departureAirport?.code;
    if (prevArrival && nextDeparture && prevArrival !== nextDeparture) {
      return i;
    }
  }
  return Math.ceil(segments.length / 2);
};

const buildFlightItemFromProposalFlight = (flight: ProposalFlight): FlProposal['item'] => {
  const segments = flight.gdsFlyAirs || [];
  const priceValue = 0;

  if (!segments.length) {
    return {
      outbound: {
        gdsFlyAirs: [],
        departureDateTime: flight.departureDateTime,
        arrivalDateTime: flight.arrivalDateTime,
        departureAirport: mapAirportWithCityDesc(flight.departureAirport),
        arrivalAirport: mapAirportWithCityDesc(flight.arrivalAirport),
        airline: flight.airLine ? { code: flight.airLine.code } : undefined,
      },
      airlineCode: flight.airLine?.code,
      priceGrossAndTax: priceValue,
    } as unknown as FlProposal['item'];
  }

  const shouldSplitBounds =
    segments.length >= 2 &&
    (isRoundTripFlightType(flight.flightType) ||
      isMultipleDestinationsFlightType(flight.flightType));

  if (shouldSplitBounds) {
    const splitAt = isMultipleDestinationsFlightType(flight.flightType)
      ? getGdsSegmentSplitIndex(segments)
      : Math.ceil(segments.length / 2);
    const outboundSegments = segments.slice(0, splitAt);
    const inboundSegments = segments.slice(splitAt);
    if (inboundSegments.length) {
      return {
        outbound: buildBoundFromGdsSegments(outboundSegments),
        inbound: buildBoundFromGdsSegments(inboundSegments, 'return'),
        airlineCode: flight.airLine?.code || outboundSegments[0]?.airLine?.code,
        priceGrossAndTax: priceValue,
      } as unknown as FlProposal['item'];
    }
  }

  return {
    outbound: buildBoundFromGdsSegments(segments),
    airlineCode: flight.airLine?.code || segments[0]?.airLine?.code,
    priceGrossAndTax: priceValue,
  } as unknown as FlProposal['item'];
};

const mapFlightProduct = (product: ProductWithMeta): FlProposal | null => {
  const flight = product.listOfItems.find((item) => item.flights?.length)?.flights?.[0];
  if (!flight) {
    return null;
  }

  const searchToken = getProductSearchToken(product, undefined, flight);
  const savedItem = (flight as { proposalItem?: FlProposal['item'] }).proposalItem;
  const priceValue = product.salePrice ?? product.amount ?? 0;
  const shouldRebuildFromGds =
    isMultipleDestinationsFlightType(flight.flightType) &&
    !!savedItem &&
    !savedItem.inbound &&
    (flight.gdsFlyAirs || []).length >= 2;

  const item =
    savedItem && !shouldRebuildFromGds ? savedItem : buildFlightItemFromProposalFlight(flight);
  if ((!savedItem || shouldRebuildFromGds) && item) {
    item.priceGrossAndTax = priceValue;
  }

  return {
    item,
    searchToken,
    price: priceValue,
    currency: product.currency,
    displayPrice: product.displayPrice,
  };
};

const mapTransferProduct = (product: ProductWithMeta): TransferProposal | null => {
  const transfer = product.listOfItems.find((item) => item.transfers?.length)?.transfers?.[0];
  if (!transfer) {
    return null;
  }
  const priceValue = product.salePrice ?? product.amount ?? 0;
  const currency = product.currency?.toLowerCase() || 'usd';
  const searchToken = getProductSearchToken(product);
  return {
    searchToken,
    price: priceValue,
    currency,
    displayPrice: product.displayPrice,
    forwardItem: {
      mainCity: {
        desc: transfer.pickUpDetails?.city?.desc || transfer.dropOffDetails?.city?.desc || '',
      },
      transfer: {
        pickUpDate: transfer.pickUpDate,
        pickUpDetails: transfer.pickUpDetails,
        dropOffDetails: transfer.dropOffDetails,
        transferVehicles: [{ vehicle: { desc: transfer.transferVehicle?.desc || 'Transfer' } }],
      } as any,
    } as any,
  };
};

export const parseProposalDataProducts = (proposalData: ProposalData) => {
  const products = (proposalData.listOfProducts || []) as ProductWithMeta[];
  const hotels: HotProposal[] = [];
  const flights: FlProposal[] = [];
  const transfers: TransferProposal[] = [];
  const hotelProducts: ProductWithMeta[] = [];
  const flightProducts: ProductWithMeta[] = [];
  const transferProducts: ProductWithMeta[] = [];
  const pTypeBySearchToken = new Map<string, string>();

  products.forEach((product) => {
    const code = product.listOfItems[0]?.proposal?.code;
    const hasTransfers = product.listOfItems.some((item) => (item.transfers || []).length > 0);
    if (hasTransfers || code === 'TRN' || code === 'TRS') {
      const transfer = mapTransferProduct(product);
      if (transfer) {
        transfers.push(transfer);
        transferProducts.push(product);
        if (product.pType) {
          pTypeBySearchToken.set(transfer.searchToken, product.pType);
        }
      }
    } else if (code === 'HTL') {
      const hotel = mapHotelProduct(product);
      if (hotel) {
        hotels.push(hotel);
        hotelProducts.push(product);
        if (product.pType) {
          pTypeBySearchToken.set(hotel.searchToken, product.pType);
        }
      }
    } else if (code === 'FLT') {
      const flight = mapFlightProduct(product);
      if (flight) {
        flights.push(flight);
        flightProducts.push(product);
        if (product.pType) {
          pTypeBySearchToken.set(flight.searchToken, product.pType);
        }
      }
    }
  });

  return {
    hotels,
    flights,
    transfers,
    hotelProducts,
    flightProducts,
    transferProducts,
    pTypeBySearchToken,
  };
};

const proposalFromProducts = (
  hotels: HotProposal[],
  flights: FlProposal[],
  transfers: TransferProposal[],
): Proposal => ({
  hotels,
  flights,
  transfers,
  dynamic: [],
  ilPackages: [],
});

const buildSlotContextsFromProposalData = (
  proposalData: ProposalData,
  idbDrafts?: IdbDraftProducts,
) => {
  const { hotels, flights, transfers } = parseProposalDataProducts(proposalData);
  const merged = mergeIdbDraftsIntoProposal(
    proposalFromProducts(hotels, flights, transfers),
    idbDrafts,
  );
  return {
    slotContexts: buildTripSlotContexts(merged),
    merged,
    proposalHotels: hotels,
    proposalFlights: flights,
    proposalTransfers: transfers,
  };
};

const buildPreviewCardKeyToProductMap = (proposalData: ProposalData): Map<string, ProposalList> => {
  const { hotels, flights, transfers, hotelProducts, flightProducts, transferProducts } =
    parseProposalDataProducts(proposalData);
  const proposal: Proposal = {
    hotels,
    flights,
    dynamic: [],
    ilPackages: [],
    transfers,
  };
  const slotContexts = buildTripSlotContexts(proposal);
  const map = new Map<string, ProposalList>();

  flights.forEach((flight, index) => {
    const product = flightProducts[index];
    if (product) {
      map.set(getPreviewFlightKey(flight, index), product);
    }
  });

  transfers.forEach((transfer, index) => {
    const product = transferProducts[index];
    if (product) {
      map.set(getPreviewTransferKey(transfer, index), product);
    }
  });

  slotContexts.forEach((ctx) => {
    ctx.chainNodes.forEach((node) => {
      node.hotels.forEach((hotel, slotIndex) => {
        const globalIndex = hotels.indexOf(hotel);
        const product = hotelProducts[globalIndex];
        if (product) {
          map.set(getPreviewHotelKey(hotel, slotIndex), product);
        }
      });
      node.transfers.forEach((transferModel) => {
        const product = transferProducts[transferModel.index];
        if (product) {
          map.set(getPreviewTransferKey(transferModel.transfer, transferModel.index), product);
        }
      });
    });
  });

  return map;
};

const resolveRowRequirement = (
  candidates: Array<{ searchToken: string }>,
  pTypeBySearchToken: Map<string, string>,
  fallback: TripSlotRequirement,
): TripSlotRequirement => {
  for (const candidate of candidates) {
    const pType = pTypeBySearchToken.get(candidate.searchToken);
    if (pType) {
      return pTypeToTripRequirement(pType);
    }
  }
  return fallback;
};

const buildRequirementByRow = (
  slotContexts: TripSlotContext[],
  pTypeBySearchToken: Map<string, string>,
) => {
  const rows: Record<string, TripSlotRequirement> = {};
  slotContexts.forEach(
    ({ rootSlotKey, chainNodes, beforeCandidates, afterCandidates, middleFlightsByTransition }) => {
      if (beforeCandidates.length) {
        rows[tripRowKeys.before(rootSlotKey)] = resolveRowRequirement(
          beforeCandidates.map((c) => c.flight),
          pTypeBySearchToken,
          'mandatory',
        );
      }
      chainNodes.forEach((node) => {
        if (node.hotels.length) {
          rows[tripRowKeys.hotel(node.slot.key)] = resolveRowRequirement(
            node.hotels,
            pTypeBySearchToken,
            'mandatory',
          );
        }
        if (node.transfers.length) {
          rows[tripRowKeys.transfer(node.slot.key)] = resolveRowRequirement(
            node.transfers.map((transferModel) => transferModel.transfer),
            pTypeBySearchToken,
            'mandatory',
          );
        }
      });
      Object.entries(middleFlightsByTransition).forEach(([transitionKey, candidates]) => {
        if (candidates.length) {
          rows[tripRowKeys.middle(transitionKey)] = resolveRowRequirement(
            candidates.map((c) => c.flight),
            pTypeBySearchToken,
            'optional',
          );
        }
      });
      if (afterCandidates.length) {
        rows[tripRowKeys.after(rootSlotKey)] = resolveRowRequirement(
          afterCandidates.map((c) => c.flight),
          pTypeBySearchToken,
          'mandatory',
        );
      }
    },
  );
  return rows;
};

type FlightSegment = 'before' | 'middle' | 'after';

const getFirstPriceSegmentForFlight = (
  flightKey: string,
  ctx: {
    beforeCandidates: TripSlotContext['beforeCandidates'];
    afterCandidates: TripSlotContext['afterCandidates'];
    middleFlightsByTransition: TripSlotContext['middleFlightsByTransition'];
  },
): { segment: FlightSegment; transitionKey?: string } | null => {
  if (ctx.beforeCandidates.some((c) => getPreviewFlightKey(c.flight, c.index) === flightKey)) {
    return { segment: 'before' };
  }
  const transitionKeys = Object.keys(ctx.middleFlightsByTransition);
  for (let index = 0; index < transitionKeys.length; index += 1) {
    const transitionKey = transitionKeys[index];
    if (
      ctx.middleFlightsByTransition[transitionKey].some(
        (c) => getPreviewFlightKey(c.flight, c.index) === flightKey,
      )
    ) {
      return { segment: 'middle', transitionKey };
    }
  }
  if (ctx.afterCandidates.some((c) => getPreviewFlightKey(c.flight, c.index) === flightKey)) {
    return { segment: 'after' };
  }
  return null;
};

const shouldShowFlightPrice = (
  flightKey: string,
  isRoundTrip: boolean,
  segment: FlightSegment,
  transitionKey: string | undefined,
  ctx: {
    beforeCandidates: TripSlotContext['beforeCandidates'];
    afterCandidates: TripSlotContext['afterCandidates'];
    middleFlightsByTransition: TripSlotContext['middleFlightsByTransition'];
  },
) => {
  if (!isRoundTrip) {
    return true;
  }
  const firstSegment = getFirstPriceSegmentForFlight(flightKey, ctx);
  if (!firstSegment) {
    return false;
  }
  if (firstSegment.segment === 'middle') {
    return segment === 'middle' && transitionKey === firstSegment.transitionKey;
  }
  return segment === firstSegment.segment;
};

const buildCompoundFlightColorMap = (
  beforeCandidates: TripSlotContext['beforeCandidates'],
  afterCandidates: TripSlotContext['afterCandidates'],
  middleFlightsByTransition: TripSlotContext['middleFlightsByTransition'],
) => {
  const map = new Map<string, string>();
  let colorIndex = 0;
  const assignColor = (candidate: { flight: FlProposal; index: number; isRoundTrip: boolean }) => {
    if (!candidate.isRoundTrip) {
      return;
    }
    const key = getPreviewFlightKey(candidate.flight, candidate.index);
    if (!map.has(key)) {
      map.set(key, orderGroupColors[colorIndex % orderGroupColors.length]);
      colorIndex += 1;
    }
  };
  beforeCandidates.forEach(assignColor);
  Object.values(middleFlightsByTransition).forEach((list) => list.forEach(assignColor));
  afterCandidates.forEach(assignColor);
  return map;
};

export const mapFlightCard = (
  candidate: TripSlotContext['beforeCandidates'][number],
  options: {
    key?: string;
    activeKey?: string;
    segment: FlightSegment;
    transitionKey?: string;
    flightCtx?: {
      beforeCandidates: TripSlotContext['beforeCandidates'];
      afterCandidates: TripSlotContext['afterCandidates'];
      middleFlightsByTransition: TripSlotContext['middleFlightsByTransition'];
    };
    compoundFlightColorByKey?: Map<string, string>;
    hideCardPrices?: boolean;
    forceShowPrice?: boolean;
  },
): TripPreviewFlightCard => {
  const key = options.key ?? getPreviewFlightKey(candidate.flight, candidate.index);
  const bounds = getBounds(candidate.flight.item);
  let bound = bounds[0];
  if (options.segment === 'after') {
    bound = bounds[bounds.length - 1];
  } else if (options.segment === 'middle') {
    const match = bounds.find(
      (b: any) =>
        `${b?.departureAirport?.code || '-'} -> ${b?.arrivalAirport?.code || '-'}` ===
        candidate.route,
    );
    if (match) {
      bound = match;
    }
  }
  const showPrice =
    options.forceShowPrice ||
    (!options.hideCardPrices &&
      !!options.flightCtx &&
      shouldShowFlightPrice(
        key,
        candidate.isRoundTrip,
        options.segment,
        options.transitionKey,
        options.flightCtx,
      ));
  const firstLeg = (
    bound as {
      gdsFlyAirs?: Array<{ flightNumber?: string; airLine?: { name?: string; desc?: string } }>;
    }
  )?.gdsFlyAirs?.[0];
  const flightNumber = firstLeg?.flightNumber || '';
  const airlineName =
    firstLeg?.airLine?.name ||
    firstLeg?.airLine?.desc ||
    (bound as { airline?: { name?: string; desc?: string; code?: string } })?.airline?.name ||
    (bound as { airline?: { name?: string; desc?: string; code?: string } })?.airline?.desc ||
    (bound as { airline?: { name?: string; desc?: string; code?: string } })?.airline?.code ||
    candidate.airlineCode ||
    '';
  return {
    key,
    route: candidate.route,
    departureLabel: getFlightCityLabel(bound?.departureAirport),
    arrivalLabel: getFlightCityLabel(bound?.arrivalAirport),
    departureTime: bound?.departureDateTime
      ? dateFormat(bound.departureDateTime, 'HH:mm', 'en')
      : undefined,
    arrivalTime: bound?.arrivalDateTime
      ? dateFormat(bound.arrivalDateTime, 'HH:mm', 'en')
      : undefined,
    durationLabel: formatFlightDurationLabel(
      (bound as { elapsedDTime?: number })?.elapsedDTime,
      bound as { gdsFlyAirs?: unknown[] },
    ),
    arrivalDetailLabel: formatArrivalDetailLabel(
      bound?.departureDateTime,
      bound?.arrivalDateTime,
      bound?.arrivalAirport?.name,
    ),
    flightInfoLabel: [flightNumber, airlineName].filter(Boolean).join(' · ') || undefined,
    airlineCode: candidate.airlineCode,
    logoCode: candidate.airlineCode || candidate.flight.item?.outbound?.airline?.code,
    scheduleLabel: formatFlightSchedule(bound?.departureDateTime, bound?.arrivalDateTime) || null,
    priceLabel: showPrice
      ? formatPriceCurrency(candidate.flight.price, getCurrency(candidate.flight.currency))
      : null,
    isActive: options.activeKey === key,
    isRoundTrip: candidate.isRoundTrip,
    compoundColor: candidate.isRoundTrip ? options.compoundFlightColorByKey?.get(key) : undefined,
    searchToken: candidate.flight.searchToken,
    flightId: candidate.flight.item?.id,
  };
};

export const mapFlightCards = (
  candidates: TripSlotContext['beforeCandidates'],
  activeKey: string | undefined,
  segment: FlightSegment,
  transitionKey: string | undefined,
  flightCtx: {
    beforeCandidates: TripSlotContext['beforeCandidates'];
    afterCandidates: TripSlotContext['afterCandidates'];
    middleFlightsByTransition: TripSlotContext['middleFlightsByTransition'];
  },
  compoundFlightColorByKey: Map<string, string>,
  displayTotalDelta: boolean,
): TripPreviewFlightCard[] =>
  candidates.map((candidate) =>
    mapFlightCard(candidate, {
      activeKey,
      segment,
      transitionKey,
      flightCtx,
      compoundFlightColorByKey,
      hideCardPrices: displayTotalDelta,
    }),
  );

const getRoomClassCxl = (roomClass?: { cxl?: string | { limitDate?: string } }) => {
  const cxl = roomClass?.cxl;
  if (!cxl) {
    return undefined;
  }
  if (typeof cxl === 'string') {
    return cxl;
  }
  return cxl.limitDate;
};

const formatHotelCxl = (cxl?: string, showCancellationDate = true, cancellationDateDelta = 0) => {
  if (!showCancellationDate || !isValidProposalCancellationDate(cxl || '')) {
    return { cxlLabel: null, cxlType: null, cxlDays: null };
  }
  const cxlDate = getAdjustedProposalCancellationDate(cxl!, cancellationDateDelta);
  const now = new Date();
  const cxlDays = Math.ceil((cxlDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (cxlDate < now) {
    return { cxlLabel: 'Full cancellation fee', cxlType: 'full' as const, cxlDays };
  }
  if (cxlDate > now) {
    return {
      cxlLabel: `Free cancel until ${dateFormat(cxlDate, 'dd MMM yyyy')}`,
      cxlType: 'free' as const,
      cxlDays,
    };
  }
  return { cxlLabel: null, cxlType: null, cxlDays: null };
};

const getRoomBoardLabel = (roomClass: any) => {
  const basis = roomClass?.board?.basis;
  const board = roomClass?.board;
  const label = basis?.desc || board?.name || basis?.code || board?.code || null;
  return label && label !== 'RO' ? label.toLowerCase() : null;
};

const mapHotelCards = (
  hotels: HotProposal[],
  activeKey: string | undefined,
  displayTotalDelta: boolean,
): TripPreviewHotelCard[] =>
  hotels.map((hotel, index) => {
    const key = getPreviewHotelKey(hotel, index);
    const checkIn = getCheckIn(hotel);
    const checkOut = getCheckOut(hotel);
    const nights =
      checkIn && checkOut
        ? Math.max(
            0,
            Math.round(
              (new Date(`${toDayKey(checkOut)}T00:00:00`).getTime() -
                new Date(`${toDayKey(checkIn)}T00:00:00`).getTime()) /
                (1000 * 60 * 60 * 24),
            ),
          )
        : 0;
    const roomClasses = hotel.item?.roomClasses || [];
    return {
      key,
      name: hotel.item?.item?.name || hotel.item?.info?.cityName || 'Hotel',
      star: hotel.item?.item?.star,
      priceLabel: `From ${getHotelStartsFrom(hotel)}`,
      isActive: activeKey === key,
      logoCode: hotel.item?.info?.picture,
      nights: nights > 0 ? nights : null,
      rooms: roomClasses.map((roomClass: any) => {
        const cxl = formatHotelCxl(
          getRoomClassCxl(roomClass),
          hotel.showCancellationDate ?? true,
          hotel.cancellationDateDelta ?? 0,
        );
        return {
          roomLabel: roomClass?.hotelRooms?.[0]?.displayName || null,
          boardLabel: getRoomBoardLabel(roomClass),
          cxlLabel: cxl.cxlLabel,
          cxlType: cxl.cxlType,
          cxlDays: cxl.cxlDays,
          priceLabel:
            typeof roomClass?.price?.value === 'number' && Number.isFinite(roomClass.price.value)
              ? formatPriceCurrency(
                  roomClass.price.value,
                  getCurrency(hotel.currency || hotel.item?.currency),
                )
              : null,
        };
      }),
      serpHotel: hotel.serpHotel || hotel.googleData?.serpResults?.serpHotel,
      tripAdvisorHotel: hotel.tripAdvisorHotel || hotel.tripAdvisorData?.tripAdvisorHotel,
      hotelInformatiom: hotel.hotelInformation,
      aI_HotelInformatiom: hotel.claudeData,
      itemCode: hotel.item?.itemCode,
      hotelIndex: index,
      supplierCode: hotel.supplierCode,
      bpid: hotel.item?.item?.bpid,
    };
  });

export const mapTransferCard = (
  transferModel: TripBuilderTransferCandidate,
  options?: { key?: string; activeKey?: string },
): TripPreviewTransferCard => {
  const key = options?.key ?? getPreviewTransferKey(transferModel.transfer, transferModel.index);
  const forward = transferModel.transfer.forwardItem?.transfer as
    | {
        pickUpDetails?: {
          airport?: { desc?: string; code?: string };
          city?: { desc?: string };
          address?: string;
        };
        dropOffDetails?: {
          airport?: { desc?: string; code?: string };
          city?: { desc?: string };
          address?: string;
        };
      }
    | undefined;
  return {
    key,
    name: transferModel.vehicle,
    pickUpLabel: getTransferLocationLabel(forward?.pickUpDetails),
    dropOffLabel: getTransferLocationLabel(forward?.dropOffDetails),
    scheduleLabel: transferModel.dateTime
      ? dateFormat(transferModel.dateTime, 'ddMMM HH:mm', 'en').toUpperCase()
      : null,
    priceLabel: formatPriceCurrency(
      transferModel.transfer.price,
      getCurrency(transferModel.transfer.currency),
    ),
    isActive: options?.activeKey === key,
    searchToken: transferModel.transfer.searchToken,
    priceKey: transferModel.transfer.forwardItem?.priceKey,
  };
};

const mapTransferCards = (
  transfers: TripSlotContext['chainNodes'][number]['transfers'],
  activeKey: string | undefined,
): TripPreviewTransferCard[] =>
  transfers.map((transferModel) => mapTransferCard(transferModel, { activeKey }));

const computeTripTotal = (
  slotContexts: TripSlotContext[],
  activeBefore: Record<string, string>,
  activeAfter: Record<string, string>,
  activeMiddle: Record<string, string>,
  activeHotel: Record<string, string>,
  activeTransfer: Record<string, string>,
  selectedRoomByHotel?: Record<string, number>,
) => {
  const countedFlightKeys = new Set<string>();
  let total = 0;
  let currency = 'usd';

  const addFlight = (candidate?: { flight: FlProposal; index: number }) => {
    if (!candidate) {
      return;
    }
    const key = getPreviewFlightKey(candidate.flight, candidate.index);
    if (countedFlightKeys.has(key)) {
      return;
    }
    countedFlightKeys.add(key);
    total += candidate.flight.price || 0;
    currency = candidate.flight.currency || currency;
  };

  slotContexts.forEach(
    ({ rootSlotKey, chainNodes, beforeCandidates, afterCandidates, middleFlightsByTransition }) => {
      const beforeKey = activeBefore[rootSlotKey];
      if (beforeKey) {
        addFlight(
          beforeCandidates.find((c) => getPreviewFlightKey(c.flight, c.index) === beforeKey),
        );
      }

      chainNodes.forEach((node) => {
        const hotelKey = activeHotel[node.slot.key];
        if (hotelKey) {
          const hotel = node.hotels.find((h, index) => getPreviewHotelKey(h, index) === hotelKey);
          if (hotel) {
            const roomIndex = selectedRoomByHotel?.[hotelKey] ?? 0;
            total += getHotelPriceForRoom(hotel, roomIndex);
            currency = hotel.currency || hotel.item?.currency || currency;
          }
        }
        const transferKey = activeTransfer[node.slot.key];
        if (transferKey) {
          const transfer = node.transfers.find(
            (transferModel) =>
              getPreviewTransferKey(transferModel.transfer, transferModel.index) === transferKey,
          );
          if (transfer) {
            total += transfer.transfer.price || 0;
            currency = transfer.transfer.currency || currency;
          }
        }
      });

      Object.entries(middleFlightsByTransition).forEach(([transitionKey, candidates]) => {
        const middleKey = activeMiddle[transitionKey];
        if (middleKey) {
          addFlight(candidates.find((c) => getPreviewFlightKey(c.flight, c.index) === middleKey));
        }
      });

      const afterKey = activeAfter[rootSlotKey];
      if (afterKey) {
        addFlight(afterCandidates.find((c) => getPreviewFlightKey(c.flight, c.index) === afterKey));
      }
    },
  );

  return { total, currency };
};

export const buildTripPreviewDefaultActive = (
  slotContexts: TripSlotContext[],
): TripPreviewActiveState => {
  const activeBefore: Record<string, string> = {};
  const activeAfter: Record<string, string> = {};
  const activeMiddle: Record<string, string> = {};
  const activeHotel: Record<string, string> = {};
  const activeTransfer: Record<string, string> = {};

  slotContexts.forEach(
    ({ rootSlotKey, chainNodes, beforeCandidates, afterCandidates, middleFlightsByTransition }) => {
      if (beforeCandidates[0]) {
        activeBefore[rootSlotKey] = getPreviewFlightKey(
          beforeCandidates[0].flight,
          beforeCandidates[0].index,
        );
      }
      chainNodes.forEach((node) => {
        if (node.hotels[0]) {
          activeHotel[node.slot.key] = getPreviewHotelKey(node.hotels[0], 0);
        }
        if (node.transfers[0]) {
          activeTransfer[node.slot.key] = getPreviewTransferKey(
            node.transfers[0].transfer,
            node.transfers[0].index,
          );
        }
      });
      Object.entries(middleFlightsByTransition).forEach(([transitionKey, candidates]) => {
        if (candidates[0]) {
          activeMiddle[transitionKey] = getPreviewFlightKey(
            candidates[0].flight,
            candidates[0].index,
          );
        }
      });
      if (afterCandidates[0]) {
        activeAfter[rootSlotKey] = getPreviewFlightKey(
          afterCandidates[0].flight,
          afterCandidates[0].index,
        );
      }
    },
  );

  return { activeBefore, activeAfter, activeMiddle, activeHotel, activeTransfer };
};

export const buildTripPreviewDefaultActiveFromProposal = (
  proposalData: ProposalData,
): TripPreviewActiveState => {
  const { hotels, flights, transfers } = parseProposalDataProducts(proposalData);
  const slotContexts = buildTripSlotContexts({
    hotels,
    flights,
    dynamic: [],
    ilPackages: [],
    transfers,
  });
  return buildTripPreviewDefaultActive(slotContexts);
};

const getSavedEntryIndex = (savedKey: string) => {
  const lastDash = savedKey.lastIndexOf('-');
  if (lastDash === -1) {
    return Number.NaN;
  }
  return Number(savedKey.slice(lastDash + 1));
};

const resolveSavedFlightKey = (
  savedKey: string | undefined,
  candidates: TripSlotContext['beforeCandidates'],
) => {
  if (!savedKey || !candidates.length) {
    return undefined;
  }
  const exact = candidates.find((c) => getPreviewFlightKey(c.flight, c.index) === savedKey);
  if (exact) {
    return getPreviewFlightKey(exact.flight, exact.index);
  }
  const idx = getSavedEntryIndex(savedKey);
  if (!Number.isNaN(idx) && candidates[idx]) {
    return getPreviewFlightKey(candidates[idx].flight, candidates[idx].index);
  }
  return undefined;
};

const resolveSavedHotelKey = (savedKey: string | undefined, hotels: HotProposal[]) => {
  if (!savedKey || !hotels.length) {
    return undefined;
  }
  const exactIndex = hotels.findIndex(
    (hotel, index) => getPreviewHotelKey(hotel, index) === savedKey,
  );
  if (exactIndex >= 0) {
    return getPreviewHotelKey(hotels[exactIndex], exactIndex);
  }
  const idx = getSavedEntryIndex(savedKey);
  if (!Number.isNaN(idx) && hotels[idx]) {
    return getPreviewHotelKey(hotels[idx], idx);
  }
  return undefined;
};

const resolveSavedTransferKey = (
  savedKey: string | undefined,
  transfers: TripSlotContext['chainNodes'][number]['transfers'],
) => {
  if (!savedKey || !transfers.length) {
    return undefined;
  }
  const exact = transfers.find(
    (transferModel) =>
      getPreviewTransferKey(transferModel.transfer, transferModel.index) === savedKey,
  );
  if (exact) {
    return getPreviewTransferKey(exact.transfer, exact.index);
  }
  const idx = getSavedEntryIndex(savedKey);
  if (!Number.isNaN(idx) && transfers[idx]) {
    return getPreviewTransferKey(transfers[idx].transfer, transfers[idx].index);
  }
  return undefined;
};

const getHotelProductSlotKey = (product: ProposalList): string | undefined => {
  const proposalCode = product.listOfItems[0]?.proposal?.code;
  if (proposalCode !== 'HTL') {
    return undefined;
  }

  const mapped = mapHotelProduct(product as ProductWithMeta);
  if (!mapped) {
    return undefined;
  }

  return getHotelSlotKey(mapped);
};

const updateProposalHotelCityName = (hotel: ProposalHotel, cityName: string): ProposalHotel => ({
  ...hotel,
  city: {
    ...hotel.city,
    name: cityName,
  },
  ...(hotel.proposalItem
    ? {
        proposalItem: {
          ...hotel.proposalItem,
          info: {
            ...hotel.proposalItem?.info,
            cityName,
          },
          addressInfo: {
            ...hotel.proposalItem?.addressInfo,
            city: cityName,
          },
        },
      }
    : {}),
});

export const updateProposalCityForSlot = (
  proposalData: ProposalData,
  slotKey: string,
  newCity: string,
): ProposalData => {
  const trimmedCity = newCity.trim();
  if (!trimmedCity) {
    return proposalData;
  }

  return {
    ...proposalData,
    listOfProducts: (proposalData.listOfProducts || []).map((product) => {
      if (getHotelProductSlotKey(product) !== slotKey) {
        return product;
      }

      return {
        ...product,
        edited: true,
        listOfItems: product.listOfItems.map((item) => {
          if (!item.hotels?.length) {
            return item;
          }

          return {
            ...item,
            hotels: item.hotels.map((hotel) => updateProposalHotelCityName(hotel, trimmedCity)),
          };
        }),
      };
    }),
  };
};

export type TripBuilderHotelConfigDraftState = {
  hotelInfoConfig?: HotelInfoConfig;
  claudeHotelInfoConfig?: ClaudeHotelInfoConfig;
};

export type TripBuilderHotelDataDraftPayload = {
  googleChecked?: { reviews: boolean; imagesIndexes: number[] };
  tripAdvisorChecked?: { details: boolean; reviews: boolean; amenities: boolean };
  sourceSerpHotel?: any;
  sourceTripadvisorHotel?: any;
  claudeHotelInfoConfig?: ClaudeHotelInfoConfig;
  hotelInfoConfig?: HotelInfoConfig;
};

export const isHotelDataConfigOnlyPayload = (payload: TripBuilderHotelDataDraftPayload) =>
  !payload.googleChecked && !payload.tripAdvisorChecked;

export type TripBuilderHotelDataState = {
  serpHotel?: any;
  tripAdvisorHotel?: any;
  serpHotelEntire?: any;
  tripAdvisorHotelEntire?: any;
};

export const applyHotelDataDraftToProposalHotel = (
  hotel: ProposalHotel,
  payload: TripBuilderHotelDataDraftPayload,
): TripBuilderHotelDataState => {
  const sourceSerpHotel = payload.sourceSerpHotel ?? hotel.serpHotelEntire ?? hotel.serpHotel;
  const isSomeGoogleChecked =
    payload.googleChecked?.reviews || !!payload.googleChecked?.imagesIndexes?.length;
  const serpHotel = !payload.googleChecked
    ? hotel.serpHotel
    : !isSomeGoogleChecked
      ? undefined
      : {
          ...sourceSerpHotel,
          images: sourceSerpHotel?.images?.filter((_img: any, index: number) =>
            payload.googleChecked?.imagesIndexes?.includes(index),
          ),
          reviews: payload.googleChecked?.reviews ? sourceSerpHotel?.reviews : null,
        };

  const sourceTripAdvisorHotel =
    payload.sourceTripadvisorHotel ?? hotel.tripAdvisorHotelEntire ?? hotel.tripAdvisorHotel;
  const isSomeTripAdvisorChecked =
    payload.tripAdvisorChecked?.details ||
    payload.tripAdvisorChecked?.reviews ||
    payload.tripAdvisorChecked?.amenities;
  const tripAdvisorHotel = !payload.tripAdvisorChecked
    ? hotel.tripAdvisorHotel
    : !isSomeTripAdvisorChecked
      ? undefined
      : {
          ...sourceTripAdvisorHotel,
          description: payload.tripAdvisorChecked?.details
            ? sourceTripAdvisorHotel?.description
            : null,
          numReviews: payload.tripAdvisorChecked?.reviews
            ? sourceTripAdvisorHotel?.numReviews
            : null,
          reviewRatingCount: payload.tripAdvisorChecked?.reviews
            ? sourceTripAdvisorHotel?.reviewRatingCount
            : null,
          amenities: payload.tripAdvisorChecked?.amenities
            ? sourceTripAdvisorHotel?.amenities
            : null,
        };

  return { serpHotel, tripAdvisorHotel };
};

export const getTripPreviewProductByCardKey = (
  proposalData: ProposalData,
  cardKey: string,
): ProposalList | undefined => buildPreviewCardKeyToProductMap(proposalData).get(cardKey);

export const getTripPreviewHotelByCardKey = (
  proposalData: ProposalData,
  cardKey: string,
  hotelDataByCardKey?: Record<string, TripBuilderHotelDataState>,
): ProposalHotel | undefined => {
  const product = getTripPreviewProductByCardKey(proposalData, cardKey);
  const hotel = product ? getHotelProductHotel(product) : undefined;
  if (!hotel) {
    return undefined;
  }

  const draft = hotelDataByCardKey?.[cardKey];
  if (!draft) {
    return hotel;
  }

  return {
    ...hotel,
    serpHotel: draft.serpHotel,
    tripAdvisorHotel: draft.tripAdvisorHotel,
    ...(draft.serpHotelEntire !== undefined ? { serpHotelEntire: draft.serpHotelEntire } : {}),
    ...(draft.tripAdvisorHotelEntire !== undefined
      ? { tripAdvisorHotelEntire: draft.tripAdvisorHotelEntire }
      : {}),
  };
};

const buildUpdatedHotelListOfItems = (
  product: ProposalList,
  updates: {
    image?: string;
    serpHotel?: any;
    tripAdvisorHotel?: any;
    serpHotelEntire?: any;
    tripAdvisorHotelEntire?: any;
  },
) =>
  product.listOfItems.map((item) => {
    if (!item.hotels?.length) {
      return item;
    }

    const nextItem = { ...item };

    if (updates.image !== undefined) {
      nextItem.listOfImages = updates.image ? [{ code: 'HTL', name: updates.image }] : [];
    }

    if (
      updates.serpHotel !== undefined ||
      updates.tripAdvisorHotel !== undefined ||
      updates.serpHotelEntire !== undefined ||
      updates.tripAdvisorHotelEntire !== undefined
    ) {
      nextItem.hotels = item.hotels.map((hotel, index) =>
        index === 0
          ? {
              ...hotel,
              ...(updates.serpHotel !== undefined ? { serpHotel: updates.serpHotel } : {}),
              ...(updates.tripAdvisorHotel !== undefined
                ? { tripAdvisorHotel: updates.tripAdvisorHotel }
                : {}),
              ...(updates.serpHotelEntire !== undefined
                ? { serpHotelEntire: updates.serpHotelEntire }
                : {}),
              ...(updates.tripAdvisorHotelEntire !== undefined
                ? { tripAdvisorHotelEntire: updates.tripAdvisorHotelEntire }
                : {}),
            }
          : hotel,
      );
    }

    return nextItem;
  });

export const parseTripPreviewHotelImagesFromInputData = (
  proposalData: ProposalData,
): Record<string, string> => {
  const result: Record<string, string> = {};

  (proposalData.listOfProducts || []).forEach((product) => {
    const slotKey = getHotelProductSlotKey(product);
    if (!slotKey) {
      return;
    }

    const imageUrl = getHotelProductCustomSlotImage(product);
    if (!imageUrl) {
      return;
    }

    result[slotKey] = imageUrl;
  });

  return result;
};

export const parseTripPreviewProposalImageFromInputData = (proposalData: ProposalData): string =>
  String(parseTripBuilderExternalParam(proposalData.externalParam_1)?.proposalImage || '');

export const shouldLoadGoogleForProposal = (proposalData: ProposalData) =>
  proposalData.listOfProducts?.some((product) =>
    product.listOfItems?.some(
      (item) => item.proposal?.code === 'HTL' && item.hotels?.some((hotel) => !hotel.serpHotel),
    ),
  );

export const getHotelCancellationSettings = (proposalData: ProposalData) => {
  const firstHotelProduct = proposalData.listOfProducts?.find(
    (product) => product.listOfItems?.[0]?.proposal?.code === 'HTL',
  );

  return {
    showCancellationDate: firstHotelProduct?.showCancellationDate ?? true,
    cancellationDateDelta: String(firstHotelProduct?.cancellationDateDelta ?? 0),
  };
};

export const parseTripPreviewDisplayTotalDeltaFromInputData = (
  proposalData: ProposalData,
): boolean => !!parseTripBuilderExternalParam(proposalData.externalParam_1)?.displayTotalDelta;

export const parseTripPreviewRowsFromInputData = (
  proposalData: ProposalData,
): Record<string, TripSlotRequirement> =>
  (parseTripBuilderExternalParam(proposalData.externalParam_1)?.rows || {}) as Record<
    string,
    TripSlotRequirement
  >;

export const parseTripPreviewRequirementByRow = (
  proposalData: ProposalData,
): Record<string, TripSlotRequirement> => {
  const { hotels, flights, transfers, pTypeBySearchToken } =
    parseProposalDataProducts(proposalData);
  const proposal: Proposal = {
    hotels,
    flights,
    dynamic: [],
    ilPackages: [],
    transfers,
  };
  const slotContexts = buildTripSlotContexts(proposal);
  return {
    ...buildRequirementByRow(slotContexts, pTypeBySearchToken),
    ...parseTripPreviewRowsFromInputData(proposalData),
  };
};

export const resolveLanguageFromProposal = (param?: string) => {
  if (!param) {
    return 'en';
  }

  try {
    const data = JSON.parse(param);
    if (data.language) {
      return String(data.language);
    }
  } catch {
    return 'en';
  }

  return 'en';
};

export const resolveTripPreviewActiveFromInputData = (
  proposalData: ProposalData,
): TripPreviewActiveState => {
  if (!proposalData.externalParam_1) {
    return emptyTripPreviewActiveState;
  }

  let data: Record<string, unknown>;
  try {
    data = JSON.parse(proposalData.externalParam_1);
  } catch {
    return emptyTripPreviewActiveState;
  }

  if (!data.isTripBuilder) {
    return emptyTripPreviewActiveState;
  }

  const savedBefore = (data.activeBeforeFlightBySlot || {}) as Record<string, string>;
  const savedAfter = (data.activeAfterFlightBySlot || {}) as Record<string, string>;
  const savedMiddle = (data.activeMiddleFlightByTransition || {}) as Record<string, string>;
  const savedHotel = (data.activeHotelBySlot || {}) as Record<string, string>;
  const savedTransfer = (data.activeTransferBySlot || {}) as Record<string, string>;

  const hasAnySelection = [savedBefore, savedAfter, savedMiddle, savedHotel, savedTransfer].some(
    (group) => Object.keys(group).length > 0,
  );
  if (!hasAnySelection) {
    return emptyTripPreviewActiveState;
  }

  const { hotels, flights, transfers } = parseProposalDataProducts(proposalData);
  const slotContexts = buildTripSlotContexts({
    hotels,
    flights,
    dynamic: [],
    ilPackages: [],
    transfers,
  });

  const activeBefore: Record<string, string> = {};
  const activeAfter: Record<string, string> = {};
  const activeMiddle: Record<string, string> = {};
  const activeHotel: Record<string, string> = {};
  const activeTransfer: Record<string, string> = {};

  slotContexts.forEach((ctx) => {
    const beforeKey = resolveSavedFlightKey(savedBefore[ctx.rootSlotKey], ctx.beforeCandidates);
    if (beforeKey) {
      activeBefore[ctx.rootSlotKey] = beforeKey;
    }

    const afterKey = resolveSavedFlightKey(savedAfter[ctx.rootSlotKey], ctx.afterCandidates);
    if (afterKey) {
      activeAfter[ctx.rootSlotKey] = afterKey;
    }

    Object.entries(ctx.middleFlightsByTransition).forEach(([transitionKey, candidates]) => {
      const middleKey = resolveSavedFlightKey(savedMiddle[transitionKey], candidates);
      if (middleKey) {
        activeMiddle[transitionKey] = middleKey;
      }
    });

    ctx.chainNodes.forEach((node) => {
      const hotelKey = resolveSavedHotelKey(savedHotel[node.slot.key], node.hotels);
      if (hotelKey) {
        activeHotel[node.slot.key] = hotelKey;
      }
      const transferKey = resolveSavedTransferKey(savedTransfer[node.slot.key], node.transfers);
      if (transferKey) {
        activeTransfer[node.slot.key] = transferKey;
      }
    });
  });

  return { activeBefore, activeAfter, activeMiddle, activeHotel, activeTransfer };
};

export const buildTripPreviewInputData = (
  proposalData: ProposalData,
  active: TripPreviewActiveState,
  options?: {
    proposalImage?: string;
    rows?: Record<string, TripSlotRequirement>;
    displayTotalDelta?: boolean;
    hotelConfigByCardKey?: Record<string, TripBuilderHotelConfigDraftState>;
    language?: string;
  },
): string => {
  let externalParam_1 = proposalData.externalParam_1;

  if (options?.hotelConfigByCardKey) {
    Object.entries(options.hotelConfigByCardKey).forEach(([cardKey, draft]) => {
      const hotel = getTripPreviewHotelByCardKey(proposalData, cardKey);
      if (!hotel) {
        return;
      }

      if (draft.hotelInfoConfig) {
        externalParam_1 = applyHotelInfoConfigToExternalParam(
          externalParam_1,
          hotel,
          draft.hotelInfoConfig,
        );
      }

      if (draft.claudeHotelInfoConfig) {
        externalParam_1 = applyClaudeHotelInfoConfigToExternalParam(
          externalParam_1,
          hotel,
          draft.claudeHotelInfoConfig,
        );
      }
    });
  }

  let existing: Record<string, unknown> = {};
  if (externalParam_1) {
    try {
      existing = JSON.parse(externalParam_1);
    } catch {
      existing = {};
    }
  }

  const payload: Record<string, unknown> = {
    ...existing,
    isTripBuilder: true,
    activeHotelBySlot: active.activeHotel,
    activeTransferBySlot: active.activeTransfer,
    activeBeforeFlightBySlot: active.activeBefore,
    activeAfterFlightBySlot: active.activeAfter,
    activeMiddleFlightByTransition: active.activeMiddle,
  };

  if (options?.proposalImage !== undefined) {
    payload.proposalImage = options.proposalImage;
  }

  if (options?.rows !== undefined) {
    payload.rows = options.rows;
  }

  if (options?.displayTotalDelta !== undefined) {
    payload.displayTotalDelta = options.displayTotalDelta;
  }

  if (options?.language !== undefined) {
    payload.language = options.language;
  }

  return JSON.stringify(payload);
};

export const buildTripPreviewProductUpdateReports = (
  proposalData: ProposalData,
  options: {
    hotelImageBySlot: Record<string, string>;
    initialHotelImageBySlot: Record<string, string>;
    requirementByRow: Record<string, TripSlotRequirement>;
    hotelCancellationSettings?: { showCancellationDate: boolean; cancellationDateDelta: number };
    hotelDataByCardKey?: Record<string, TripBuilderHotelDataState>;
  },
): ReportsList[] => {
  const { hotels, flights, transfers } = parseProposalDataProducts(proposalData);
  const proposal: Proposal = {
    hotels,
    flights,
    dynamic: [],
    ilPackages: [],
    transfers,
  };
  const slotContexts = buildTripSlotContexts(proposal);
  const pTypeBySearchToken = buildPTypeBySearchToken(
    slotContexts as TripSlotContext[],
    options.requirementByRow,
  );
  const cardKeyByProductId = new Map<string, string>();
  buildPreviewCardKeyToProductMap(proposalData).forEach((mappedProduct, cardKey) => {
    if (!cardKeyByProductId.has(mappedProduct.id)) {
      cardKeyByProductId.set(mappedProduct.id, cardKey);
    }
  });
  const reports: ReportsList[] = [];

  (proposalData.listOfProducts || []).forEach((product) => {
    const code = product.listOfItems[0]?.proposal?.code;
    const hasTransfers = product.listOfItems.some((item) => (item.transfers || []).length > 0);

    let searchToken: string | undefined;
    let reportName: number | undefined;
    let productKey: 'hotelProduct' | 'flightProduct' | 'transferProduct' | undefined;

    if (hasTransfers || code === 'TRN') {
      searchToken = mapTransferProduct(product)?.searchToken;
      reportName = 5;
      productKey = 'transferProduct';
    } else if (code === 'HTL') {
      searchToken = mapHotelProduct(product)?.searchToken;
      reportName = 0;
      productKey = 'hotelProduct';
    } else if (code === 'FLT') {
      searchToken = mapFlightProduct(product)?.searchToken;
      reportName = 1;
      productKey = 'flightProduct';
    }

    if (!searchToken || reportName === undefined || !productKey) {
      return;
    }

    const nextPType = pTypeBySearchToken[searchToken];
    const currentPType = (product as ProductWithMeta).pType || 'None';
    const pTypeChanged = !!nextPType && nextPType !== currentPType;

    let listOfItemsKeysForUpdate = product.listOfItems;
    let imageChanged = false;
    let hotelDataChanged = false;
    let hotelCancellationChanged = false;

    if (productKey === 'hotelProduct') {
      const slotKey = getHotelProductSlotKey(product);
      let desiredImage: string | undefined;
      if (slotKey) {
        const baselineImage = options.initialHotelImageBySlot[slotKey] ?? '';
        const nextImage = options.hotelImageBySlot[slotKey] ?? '';
        if (nextImage !== baselineImage) {
          imageChanged = true;
          desiredImage = nextImage;
        }
      }

      const cardKey = cardKeyByProductId.get(product.id);
      const hotelData = cardKey ? options.hotelDataByCardKey?.[cardKey] : undefined;
      hotelDataChanged = !!hotelData;
      const nextShowCancellationDate = options.hotelCancellationSettings?.showCancellationDate;
      const nextCancellationDateDelta = options.hotelCancellationSettings?.cancellationDateDelta;
      if (
        typeof nextShowCancellationDate === 'boolean' &&
        typeof nextCancellationDateDelta === 'number'
      ) {
        hotelCancellationChanged =
          (product.showCancellationDate ?? true) !== nextShowCancellationDate ||
          (product.cancellationDateDelta ?? 0) !== nextCancellationDateDelta;
      }

      if (imageChanged || hotelDataChanged) {
        listOfItemsKeysForUpdate = buildUpdatedHotelListOfItems(product, {
          ...(imageChanged ? { image: desiredImage } : {}),
          ...(hotelData
            ? {
                serpHotel: hotelData.serpHotel,
                tripAdvisorHotel: hotelData.tripAdvisorHotel,
                serpHotelEntire: hotelData.serpHotelEntire,
                tripAdvisorHotelEntire: hotelData.tripAdvisorHotelEntire,
              }
            : {}),
        });
      }
    }

    if (!pTypeChanged && !imageChanged && !hotelDataChanged && !hotelCancellationChanged) {
      return;
    }

    const baseProduct = {
      id: product.id,
      amount: product.amount,
      currency: product.currency,
      salePrice: product.salePrice,
      note: product.note,
      displayPrice: product.displayPrice,
      ...(productKey === 'hotelProduct' && options.hotelCancellationSettings
        ? {
            showCancellationDate: options.hotelCancellationSettings.showCancellationDate,
            cancellationDateDelta: options.hotelCancellationSettings.cancellationDateDelta,
          }
        : {}),
      ...(pTypeChanged && nextPType ? { pType: nextPType } : {}),
      listOfItemsKeysForUpdate,
    };

    reports.push({
      reportName,
      [productKey]: baseProduct,
    } as ReportsList);
  });

  return reports;
};

export const buildTripPreviewProductDeleteReports = (
  proposalData: ProposalData,
  excludedKeys: string[],
): ReportsList[] => {
  if (!excludedKeys.length) {
    return [];
  }

  const keyToProduct = buildPreviewCardKeyToProductMap(proposalData);
  const seenProductIds = new Set<string>();
  const reports: ReportsList[] = [];

  excludedKeys.forEach((key) => {
    const product = keyToProduct.get(key);
    if (!product || seenProductIds.has(product.id)) {
      return;
    }

    seenProductIds.add(product.id);

    const code = product.listOfItems[0]?.proposal?.code;
    const hasTransfers = product.listOfItems.some((item) => (item.transfers || []).length > 0);
    const baseProduct = {
      id: product.id,
      amount: product.amount,
      currency: product.currency,
      salePrice: product.salePrice,
      note: product.note,
      displayPrice: product.displayPrice,
      itemsKeysForDelete: [product.id],
    };

    if (hasTransfers || code === 'TRN' || code === 'TRS') {
      reports.push({ reportName: 5, transferProduct: baseProduct });
      return;
    }

    if (code === 'HTL') {
      reports.push({ reportName: 0, hotelProduct: baseProduct });
      return;
    }

    if (code === 'FLT') {
      reports.push({ reportName: 1, flightProduct: baseProduct });
    }
  });

  return reports;
};

const getProposalProductRoomPrices = (product: ProposalList) =>
  (product.listOfItems || []).flatMap((item) =>
    (item.hotels || []).flatMap((hotel) =>
      (hotel.rooms || []).map((room) => room.supplements?.[0]?.code || ''),
    ),
  );

export type ProposalProductSnapshot = {
  currency: string;
  amount: number;
  salePrice: number;
  roomPrices: string[];
};

export const createProposalProductSnapshot = (proposalData: ProposalData) => {
  const snapshot = new Map<string, ProposalProductSnapshot>();

  (proposalData.listOfProducts || []).forEach((product) => {
    snapshot.set(product.id, {
      currency: (product.currency || 'usd').toLowerCase(),
      amount: product.amount,
      salePrice: product.salePrice,
      roomPrices: getProposalProductRoomPrices(product),
    });
  });

  return snapshot;
};

export const hasProposalProductCurrencyOrPriceChange = (
  product: ProposalList,
  initial?: ProposalProductSnapshot,
) => {
  if (!initial) {
    return false;
  }

  const currency = (product.currency || 'usd').toLowerCase();
  if (currency !== initial.currency) {
    return true;
  }

  if (product.amount !== initial.amount || product.salePrice !== initial.salePrice) {
    return true;
  }

  const roomPrices = getProposalProductRoomPrices(product);
  return roomPrices.some((price, index) => price !== initial.roomPrices[index]);
};

export const withCurrencyChangedProductsMarked = (
  proposalData: ProposalData,
  initialSnapshot: Map<string, ProposalProductSnapshot>,
): ProposalData => ({
  ...proposalData,
  listOfProducts: (proposalData.listOfProducts || []).map((product) => ({
    ...product,
    edited:
      product.edited ||
      hasProposalProductCurrencyOrPriceChange(product, initialSnapshot.get(product.id)),
  })),
});

export const buildTripPreviewEditedProductReports = (proposalData: ProposalData): ReportsList[] =>
  (proposalData.listOfProducts || [])
    .filter((product) => !product.removed && product.edited)
    .map((product) => {
      const baseProduct = {
        id: product.id,
        amount: product.amount,
        currency: product.currency,
        salePrice: product.salePrice,
        note: product.note,
        displayPrice: product.displayPrice,
        listOfItemsKeysForUpdate: product.listOfItems,
      };

      if (product.listOfItems.length > 1) {
        return {
          reportName: 2,
          dynamicProduct: baseProduct,
        } as ReportsList;
      }

      const code = product.listOfItems[0]?.proposal?.code;
      if (code === 'HTL') {
        return {
          reportName: 0,
          hotelProduct: {
            ...baseProduct,
            showCancellationDate: product.showCancellationDate,
            cancellationDateDelta: product.cancellationDateDelta,
          },
        } as ReportsList;
      }

      if (code === 'FLT') {
        return {
          reportName: 1,
          flightProduct: baseProduct,
        } as ReportsList;
      }

      if (code === 'TRS' || code === 'TRN') {
        return {
          reportName: 5,
          transferProduct: baseProduct,
        } as ReportsList;
      }

      return {
        reportName: 8,
        ilPackageProduct: baseProduct,
      } as ReportsList;
    });

const buildPreviewFlightSection = (
  candidates: TripSlotContext['beforeCandidates'],
  rowKey: string,
  sectionTitle: string,
  segment: FlightSegment,
  transitionKey: string | undefined,
  isIncluded: (candidate: TripSlotContext['beforeCandidates'][number]) => boolean,
  requirementByRow: Record<string, TripSlotRequirement>,
  defaultRequirement: TripSlotRequirement,
  activeKey: string | undefined,
  flightCtx: {
    beforeCandidates: TripSlotContext['beforeCandidates'];
    afterCandidates: TripSlotContext['afterCandidates'];
    middleFlightsByTransition: TripSlotContext['middleFlightsByTransition'];
  },
  compoundFlightColorByKey: Map<string, string>,
  hideCardPrices: boolean,
): TripPreviewSection | null => {
  const filtered = candidates.filter(isIncluded);
  if (!filtered.length) {
    return null;
  }
  return {
    requirementLabel: getRequirementLabel(requirementByRow[rowKey] ?? defaultRequirement),
    sectionTitle,
    flights: mapFlightCards(
      filtered,
      activeKey,
      segment,
      transitionKey,
      flightCtx,
      compoundFlightColorByKey,
      hideCardPrices,
    ),
    hotels: [],
    transfers: [],
  };
};

export const buildTripPreviewModel = (
  proposalData: ProposalData,
  active: TripPreviewActiveState,
  options?: {
    requirementByRow?: Record<string, TripSlotRequirement>;
    displayTotalDelta?: boolean;
    selectedRoomByHotel?: Record<string, number>;
    idbDrafts?: TripBuilderIdbDrafts;
  },
): TripPreviewModel => {
  const locale = 'en';
  const { pTypeBySearchToken } = parseProposalDataProducts(proposalData);
  const { slotContexts, proposalHotels, proposalFlights, proposalTransfers } =
    buildSlotContextsFromProposalData(proposalData, options?.idbDrafts);
  const proposalHotelTokens = new Set(proposalHotels.map((hotel) => hotel.searchToken));
  const proposalFlightTokens = new Set(proposalFlights.map((flight) => flight.searchToken));
  const proposalTransferTokens = new Set(proposalTransfers.map((transfer) => transfer.searchToken));
  const isProposalHotel = (hotel: HotProposal) => proposalHotelTokens.has(hotel.searchToken);
  const isProposalFlight = (candidate: TripSlotContext['beforeCandidates'][number]) =>
    proposalFlightTokens.has(candidate.flight.searchToken);
  const isProposalTransfer = (
    transferModel: TripSlotContext['chainNodes'][number]['transfers'][number],
  ) => proposalTransferTokens.has(transferModel.transfer.searchToken);

  let displayTotalDelta = parseTripPreviewDisplayTotalDeltaFromInputData(proposalData);
  if (options?.displayTotalDelta !== undefined) {
    displayTotalDelta = options.displayTotalDelta;
  }
  const hideCardPrices = displayTotalDelta;

  const requirementByRow = {
    ...buildRequirementByRow(slotContexts, pTypeBySearchToken),
    ...parseTripPreviewRowsFromInputData(proposalData),
    ...options?.requirementByRow,
  };
  const { activeBefore, activeAfter, activeMiddle, activeHotel, activeTransfer } = active;

  const tripTotal = computeTripTotal(
    slotContexts,
    activeBefore,
    activeAfter,
    activeMiddle,
    activeHotel,
    activeTransfer,
    options?.selectedRoomByHotel,
  );

  const slots: TripPreviewSlotView[] = slotContexts.map((ctx) => {
    const flightCtx = {
      beforeCandidates: ctx.beforeCandidates,
      afterCandidates: ctx.afterCandidates,
      middleFlightsByTransition: ctx.middleFlightsByTransition,
    };
    const compoundFlightColorByKey = buildCompoundFlightColorMap(
      ctx.beforeCandidates,
      ctx.afterCandidates,
      ctx.middleFlightsByTransition,
    );
    const formatBoundaryDate = (value?: string): TripPreviewDateBadge | null => {
      if (!value) {
        return null;
      }
      return {
        day: dateFormat(value, 'dd', locale),
        month: dateFormat(value, 'MMM', locale).toUpperCase(),
        weekDay: dateFormat(value, 'EEE', locale).toUpperCase(),
      };
    };
    const getFlightBoundaryDate = (
      flight: FlProposal | undefined,
      boundary: 'start' | 'finish',
    ) => {
      if (!flight) {
        return '';
      }
      const bounds = getBounds(flight.item);
      if (!bounds.length) {
        return '';
      }
      if (boundary === 'start') {
        return bounds[0]?.departureDateTime || '';
      }
      const last = bounds[bounds.length - 1];
      return last?.arrivalDateTime || last?.departureDateTime || '';
    };
    const activeBeforeFlight = ctx.beforeCandidates.find(
      (candidate) =>
        getPreviewFlightKey(candidate.flight, candidate.index) === activeBefore[ctx.rootSlotKey],
    );
    const activeAfterFlight = ctx.afterCandidates.find(
      (candidate) =>
        getPreviewFlightKey(candidate.flight, candidate.index) === activeAfter[ctx.rootSlotKey],
    );
    const firstNode = ctx.chainNodes[0];
    const lastNode = ctx.chainNodes[ctx.chainNodes.length - 1];
    const activeStartHotel = firstNode?.hotels.find(
      (item, index) => getPreviewHotelKey(item, index) === activeHotel[firstNode.slot.key],
    );
    const activeEndHotel = lastNode?.hotels.find(
      (item, index) => getPreviewHotelKey(item, index) === activeHotel[lastNode.slot.key],
    );
    const startDateBadge = formatBoundaryDate(
      getFlightBoundaryDate((activeBeforeFlight || ctx.beforeCandidates[0])?.flight, 'start') ||
        getCheckIn(activeStartHotel || firstNode?.hotels[0]),
    );
    const endDateBadge = formatBoundaryDate(
      getFlightBoundaryDate((activeAfterFlight || ctx.afterCandidates[0])?.flight, 'finish') ||
        getCheckOut(activeEndHotel || lastNode?.hotels[0]),
    );

    const beforeSection = buildPreviewFlightSection(
      ctx.beforeCandidates,
      tripRowKeys.before(ctx.rootSlotKey),
      'Before hotel',
      'before',
      undefined,
      isProposalFlight,
      requirementByRow,
      'mandatory',
      activeBefore[ctx.rootSlotKey],
      flightCtx,
      compoundFlightColorByKey,
      hideCardPrices,
    );

    const afterSection = buildPreviewFlightSection(
      ctx.afterCandidates,
      tripRowKeys.after(ctx.rootSlotKey),
      'After hotel',
      'after',
      undefined,
      isProposalFlight,
      requirementByRow,
      'mandatory',
      activeAfter[ctx.rootSlotKey],
      flightCtx,
      compoundFlightColorByKey,
      hideCardPrices,
    );

    const chainNodesView: TripPreviewChainNode[] = ctx.chainNodes.map((node, nodeIndex) => {
      const nextNode = ctx.chainNodes[nodeIndex + 1];
      const transitionKey = nextNode ? `${node.slot.key}|${nextNode.slot.key}` : '';
      const middleFlights = transitionKey ? ctx.middleFlightsByTransition[transitionKey] || [] : [];

      const middleSection = buildPreviewFlightSection(
        middleFlights,
        tripRowKeys.middle(transitionKey),
        'Between hotels',
        'middle',
        transitionKey,
        isProposalFlight,
        requirementByRow,
        'optional',
        activeMiddle[transitionKey],
        flightCtx,
        compoundFlightColorByKey,
        hideCardPrices,
      );
      const middleSections = middleSection ? [{ ...middleSection, transitionKey }] : [];

      const proposalHotelsInNode = node.hotels.filter(isProposalHotel);
      const hotelSection = proposalHotelsInNode.length
        ? {
            requirementLabel: getRequirementLabel(
              requirementByRow[tripRowKeys.hotel(node.slot.key)] ?? 'mandatory',
            ),
            flights: [],
            hotels: mapHotelCards(proposalHotelsInNode, activeHotel[node.slot.key], hideCardPrices),
            transfers: [],
          }
        : null;
      const proposalTransfersInNode = node.transfers.filter(isProposalTransfer);
      const transferSection = proposalTransfersInNode.length
        ? {
            requirementLabel: getRequirementLabel(
              requirementByRow[tripRowKeys.transfer(node.slot.key)] ?? 'mandatory',
            ),
            flights: [],
            hotels: [],
            transfers: mapTransferCards(proposalTransfersInNode, activeTransfer[node.slot.key]),
          }
        : null;

      return {
        dateLabel: `${formatSlotDate(node.slot.checkIn, locale)} - ${formatSlotDate(node.slot.checkOut, locale)}`,
        city: node.slot.city,
        checkIn: node.slot.checkIn,
        checkOut: node.slot.checkOut,
        slotKey: node.slot.key,
        hotelSection,
        transferSection,
        middleSections,
      };
    });

    return {
      rootSlotKey: ctx.rootSlotKey,
      startDateBadge,
      endDateBadge,
      beforeSection,
      afterSection,
      chainNodes: chainNodesView,
    };
  });

  return {
    title: proposalData.description || 'Proposal',
    displayTotalDelta,
    totalLabel: formatPriceCurrency(tripTotal.total, getCurrency(tripTotal.currency)),
    totalCurrency: tripTotal.currency,
    slots,
  };
};

export const clearRoundTripFlightKeys = (
  active: TripPreviewActiveState,
  rootSlotKey: string,
  slot: TripPreviewSlotView,
): TripPreviewActiveState => {
  const next = {
    activeBefore: { ...active.activeBefore },
    activeAfter: { ...active.activeAfter },
    activeMiddle: { ...active.activeMiddle },
    activeHotel: { ...active.activeHotel },
    activeTransfer: { ...active.activeTransfer },
  };

  const beforeKey = next.activeBefore[rootSlotKey];
  if (
    beforeKey &&
    slot.beforeSection?.flights.some((flight) => flight.key === beforeKey && flight.isRoundTrip)
  ) {
    delete next.activeBefore[rootSlotKey];
  }

  const afterKey = next.activeAfter[rootSlotKey];
  if (
    afterKey &&
    slot.afterSection?.flights.some((flight) => flight.key === afterKey && flight.isRoundTrip)
  ) {
    delete next.activeAfter[rootSlotKey];
  }

  slot.chainNodes.forEach((node) => {
    node.middleSections.forEach((section) => {
      if (!section.transitionKey) {
        return;
      }
      const middleKey = next.activeMiddle[section.transitionKey];
      if (
        middleKey &&
        section.flights.some((flight) => flight.key === middleKey && flight.isRoundTrip)
      ) {
        delete next.activeMiddle[section.transitionKey];
      }
    });
  });

  return next;
};

export const syncRoundTripFlight = (
  active: TripPreviewActiveState,
  slot: TripPreviewSlotView,
  rootSlotKey: string,
  flightKey: string,
): TripPreviewActiveState => {
  const next = {
    ...active,
    activeBefore: { ...active.activeBefore, [rootSlotKey]: flightKey },
    activeAfter: { ...active.activeAfter },
    activeMiddle: { ...active.activeMiddle },
    activeTransfer: { ...active.activeTransfer },
  };

  if (slot.afterSection?.flights.some((flight) => flight.key === flightKey)) {
    next.activeAfter[rootSlotKey] = flightKey;
  }

  slot.chainNodes.forEach((node) => {
    node.middleSections.forEach((section) => {
      if (section.transitionKey && section.flights.some((flight) => flight.key === flightKey)) {
        next.activeMiddle[section.transitionKey] = flightKey;
      }
    });
  });

  return next;
};

export const clearFlightKeyFromActive = (
  active: TripPreviewActiveState,
  slot: TripPreviewSlotView,
  flightKey: string,
): TripPreviewActiveState => {
  const next = {
    activeBefore: { ...active.activeBefore },
    activeAfter: { ...active.activeAfter },
    activeMiddle: { ...active.activeMiddle },
    activeHotel: { ...active.activeHotel },
    activeTransfer: { ...active.activeTransfer },
  };

  if (next.activeBefore[slot.rootSlotKey] === flightKey) {
    delete next.activeBefore[slot.rootSlotKey];
  }
  if (next.activeAfter[slot.rootSlotKey] === flightKey) {
    delete next.activeAfter[slot.rootSlotKey];
  }
  slot.chainNodes.forEach((node) => {
    node.middleSections.forEach((section) => {
      if (section.transitionKey && next.activeMiddle[section.transitionKey] === flightKey) {
        delete next.activeMiddle[section.transitionKey];
      }
    });
  });

  return next;
};
export const getTripBuilderPlainPreviewStorageKey = (proposalKey: string) =>
  `tripBuilderPlainPreview:${proposalKey}`;

export const buildTripBuilderPlainProposalData = (
  proposalData: ProposalData,
  active: TripPreviewActiveState,
  excludedCardKeys: Record<string, true>,
  options?: {
    language?: string;
    proposalImage?: string;
    displayTotalDelta?: boolean;
  },
): ProposalData => {
  const keyToProduct = buildPreviewCardKeyToProductMap(proposalData);
  const activeKeys = new Set(
    [
      ...Object.values(active.activeBefore),
      ...Object.values(active.activeAfter),
      ...Object.values(active.activeMiddle),
      ...Object.values(active.activeHotel),
      ...Object.values(active.activeTransfer),
    ].filter(Boolean),
  );
  const excludedKeys = new Set(Object.keys(excludedCardKeys));
  const productIds = new Set<string>();

  activeKeys.forEach((cardKey) => {
    if (excludedKeys.has(cardKey)) {
      return;
    }
    const product = keyToProduct.get(cardKey);
    if (product?.id) {
      productIds.add(product.id);
    }
  });

  let externalParams: Record<string, unknown> = {};
  try {
    externalParams = JSON.parse(proposalData.externalParam_1 || '{}');
  } catch {
    externalParams = {};
  }

  const externalParam_1 = JSON.stringify({
    ...externalParams,
    isTripBuilder: false,
    ...(options?.language ? { language: options.language } : {}),
    ...(options?.proposalImage !== undefined ? { proposalImage: options.proposalImage } : {}),
    ...(options?.displayTotalDelta !== undefined
      ? { displayTotalDelta: options.displayTotalDelta }
      : {}),
    activeHotelBySlot: active.activeHotel,
    activeTransferBySlot: active.activeTransfer,
    activeBeforeFlightBySlot: active.activeBefore,
    activeAfterFlightBySlot: active.activeAfter,
    activeMiddleFlightByTransition: active.activeMiddle,
  });

  return {
    ...proposalData,
    externalParam_1,
    listOfProducts: (proposalData.listOfProducts || []).filter(
      (product) => productIds.has(product.id) && !product.removed,
    ),
  };
};
